import React, { Component } from 'react';
import { query, repository, subscribe } from '@trlogic/client-js';
import { InstanceContainer } from '../../../../config/binding/InstanceBinding';
import Injectables from '../../../../constant/Injectables';
import CaseService from '../../../../service/CaseService';
import ServiceContainer from '../../../../config/ServiceContainer';
import { ServiceNames } from '../../../../constant/ServiceNames';
import CollectionNames from '../../../../constant/CollectionNames';
import QueryBuilder = query.QueryBuilder;
import { MLOpsDatasourceHistorical, MLOpsModel } from '../../../../domain/mlops/MLOpsDomains';
import { CaseSettings } from '../../../../domain/case/CaseDomains';
import { TransactionData } from '../../../../domain/stream/transaction/TransactionDomains';
import Filter = query.Filter;
import Order = query.Order;
import empty = query.empty;
import TransactionPageUtil from '../../../../pages/transaction/TransactionPageUtil';
import CustomLoading from '../../../custom/CustomLoading';
import CustomFilter from '../../../custom/filter/CustomFilter';
import FilterUtil from '../../../../util/FilterUtil';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import { RootState } from '../../../../redux/store';
import { changePageFilters } from '../../../../redux/actions/PageActions';
import MLOpsDatasourceHistoricalTableComponent from './MLOpsDatasourceHistoricalTableComponent';
import eq = query.eq;

class MlOpsDatasourceHistoricalComponent extends Component<Props, State> {
  public state: State = new State();

  private caseSettingsSubscription: subscribe.Subscription | undefined;
  private channelSubscription: subscribe.Subscription | undefined;
  private computation: subscribe.Computation | undefined;

  private readonly repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);
  private readonly transactionService: CaseService = ServiceContainer.getInstance().get(ServiceNames.CASE);

  public render() {
    const { loading, transactions, totalRecord, rows, currentPage, sort } = this.state;
    const { type, aiModel } = this.props;
    if (loading) {
      return <CustomLoading />;
    }

    return (
      <div className='flex flex-col gap-[12px]' id={type}>
        <div className='flex flex-col gap-[4px]'>
          <CustomFilter key={type} filterKey={type} onChange={this.onChangeFilter} defaultFilterDisabled />
        </div>
        <MLOpsDatasourceHistoricalTableComponent
          key={type}
          transactions={transactions}
          baseFeatures={aiModel.baseFeatures}
          loading={loading}
          total={totalRecord}
          rows={rows}
          currentPage={currentPage}
          sort={sort}
          filterKey={type}
          onPageChange={this.onPageChange}
          onSortChange={this.onSortChange}
          onChangeFilter={this.onChangeFilter}
        />
      </div>
    );
  }

  public componentDidMount() {
    const { type, page } = this.props;
    const pageFilter = page.filters[type];
    this.setState({ filter: pageFilter ? FilterUtil.parse(pageFilter.filter) : empty() });
    void this.syncFilterFields();
    this.startTracker();
  }

  public componentDidUpdate(prevProps: Readonly<Props>) {
    const { aiModel } = this.props;
    if (this.hasBaseFeaturesChanged(prevProps.aiModel.baseFeatures, aiModel.baseFeatures)) {
      void this.syncFilterFields();
    }
  }

  private hasBaseFeaturesChanged = (previous: MLOpsModel['baseFeatures'], current: MLOpsModel['baseFeatures']): boolean => {
    if (previous.length != current.length) return true;
    return previous.some((feature, index) => feature.name != current[index]?.name);
  };

  private syncFilterFields = async (): Promise<void> => {
    const { aiModel, type } = this.props;
    await FilterUtil.getMLOpsDatasourceFields(aiModel.channel, aiModel.baseFeatures, type, true);
  };

  private startTracker = () => {
    const { aiModel } = this.props;
    const currentTransactionSettingsQuery = QueryBuilder.select().from(CollectionNames.CASE_SETTINGS).where(eq('dataSource', aiModel.channel)).build();
    const channelQuery = QueryBuilder.select().from(CollectionNames.TRANSACTIONS).where(eq('id', aiModel.channel)).build();

    this.caseSettingsSubscription = subscribe.Formica.subscribe(CollectionNames.CASE_SETTINGS, currentTransactionSettingsQuery);
    this.channelSubscription = subscribe.Formica.subscribe(CollectionNames.TRANSACTIONS, channelQuery);
    this.computation = subscribe.Tracker.autorun(this.tracker, [this.caseSettingsSubscription, this.channelSubscription]);
  };

  private stopTracker = () => {
    this.caseSettingsSubscription?.stop();
    this.channelSubscription?.stop();
    this.computation?.stop();
  };

  public componentWillUnmount() {
    this.stopTracker();
  }

  private tracker = async () => {
    if (this.caseSettingsSubscription?.ready() && this.channelSubscription?.ready()) {
      await this.buildData();
    }
  };

  private buildData = async () => {
    try {
      const { aiModel } = this.props;
      const { filter, sort, currentPage, rows } = this.state;
      const settings = await this.repository.find<CaseSettings>(
        QueryBuilder.select().from(CollectionNames.CASE_SETTINGS).where(eq('dataSource', aiModel.channel)).build(),
      );
      if (settings) {
        const _query = TransactionPageUtil.getTransactionQuery(settings, [settings], filter, rows, currentPage, sort);
        const { data, count } = await this.transactionService.getPageData(aiModel.channel, _query);
        this.setState({ transactions: data, totalRecord: count, loading: false });
      } else {
        console.error('Error loading datasource, please set Configuration');
        this.setState({ loading: false, transactions: [], totalRecord: 0 });
      }
    } catch (error) {
      console.error('Error loading datasource:', error);
      this.setState({ loading: false, transactions: [], totalRecord: 0 });
    }
  };

  private onSortChange = (order: Order[]) => {
    this.setState({ sort: order, loading: true }, this.buildData);
  };

  private onPageChange = (page: number, rows: number) => {
    this.setState({ currentPage: page, rows: rows, loading: true }, this.buildData);
  };

  private onChangeFilter = (filter: Filter) => {
    const {
      type,
      page: { filters },
      changePageFilters,
    } = this.props;

    const _filter = filters[type];
    if (_filter) {
      changePageFilters(filters);
      filter = FilterUtil.parse(_filter.filter);
    }

    this.setState({ filter }, this.buildData);
  };
}

class State {
  loading: boolean = true;
  transactions: TransactionData[] = [];
  totalRecord: number = 0;
  rows: number = 10;
  currentPage: number = 1;
  sort: Order[] = [];
  filter: Filter = empty();
}

interface Props extends ReduxProps {
  aiModel: MLOpsModel;
  datasource: MLOpsDatasourceHistorical;
  onChange: (datasource: MLOpsDatasourceHistorical) => void;
  type: 'VALIDATION' | 'TRAINING';
}

const mapStateToProps = (state: RootState) => ({
  isUpdate: state.aiModel.isUpdate,
  lastCategory: state.page.lastCategory,
  page: state.page,
});
const mapDispatchToProps = {
  changePageFilters,
};

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(MlOpsDatasourceHistoricalComponent);
